import React, { useEffect } from "react";
import SectionToggle from "../../components/sectionToggle/sectionToggle";
import LoadingAnimation from "../../components/loadingAnimation/loadingAnimation";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { UpdateButtons } from "./smartComponents/UpdateButtons";
import { actionCreators } from "./redux/DemographicsSagas";
import PieCharts from "./smartComponents/PieCharts";
import Filters from "./smartComponents/Filters";
import { addSticky } from "../../utils/addSticky";
import TableWithCustomSearch from "../../components/coreUiTableWithCustomSearchAndDateConversion/TableWithCustomSearch";
import LearnMore from "../../components/learnMore/learnMore";
import PropTypes from "prop-types";
import ToggleableButton from "../../components/toggleableButton/toggleableButton";

const DemographicsModule = (props) => {
  const dispatch = useDispatch();
  const {
    filters = [],
    selectedFilters,
    isAllSitesMode,
    pieChartsData,
    isLoading,
    updatesLoading,
    isLoadingFilters,
    referralCategory,
    referralCategoryAfterUpdate,
    chartsData: {
      tableData: {
        content: { data = [], colHeaders = [], columns, rawDataLink }
      }
    }
  } = useSelector((state) => state.demographics, shallowEqual);

  const showRawDataLink = selectedFilters.group_by.name === "Not Grouped";

  useEffect(() => {
    if (!isLoading && !isAllSitesMode) {
      addSticky();
    }
  }, [isLoading]);

  const filtersOptions = [
    {
      label: referralCategory ? "Referral Categories" : "Referral Sources",
      name: "selected_categories",
      selectedFilters: selectedFilters.selected_categories,
      options: filters.selected_categories,
      width: "145px"
    },
    {
      label: "ZIP Code",
      name: "zip_codes",
      selectedFilters: selectedFilters.zip_codes,
      options: filters.zip_codes
    },
    {
      label: "Age Groups",
      name: "age_groups",
      selectedFilters: selectedFilters.age_groups,
      options: filters.age_groups
    },
    {
      label: "Gender",
      name: "genders",
      selectedFilters: selectedFilters.genders,
      options: filters.genders
    },
    {
      label: "Last Visit",
      name: "last_visits_months",
      selectedFilters: selectedFilters.last_visits_months,
      options: filters.last_visits_months
    },
    {
      label: "Birth Month",
      name: "birth_months",
      selectedFilters: selectedFilters.birth_months,
      options: filters.birth_months
    }
  ];

  const pieChartsOptions = [
    {
      data: pieChartsData.chartByReferralCategory,
      title: `Clients by referral ${referralCategoryAfterUpdate ? "category" : "source"}`
    },
    { data: pieChartsData.chartByZipCode, title: "Clients by zip code" },
    { data: pieChartsData.chartByAge, title: "Clients by age" },
    { data: pieChartsData.chartByGender, title: "Clients by gender" }
  ];

  useEffect(() => {
    dispatch(actionCreators.fetchCurrentUser(props));
  }, []);
  const content = () => {
    return isAllSitesMode ? (
      <p className="error error--big" style={{ ...errorMessageStyles }}>
        Not Available in All Sites View, when user has access to more than 100 locations
      </p>
    ) : (
      <div>
        <div id="sticky-select-bar" className="edit-selections-bar">
          <div className="selection-bar-filters">
            <div className="top-filter">
              <div className="top-filter__left">
                <div className="top-filter__left-top">
                  <Filters
                    allSelectedPlaceholder="All Clients"
                    selectAllText="All Clients"
                    label="Client Type"
                    name="client_types"
                    selectedFilters={selectedFilters.client_types}
                    options={filters.client_types}
                    numberDisplayed={1}
                  />
                  <div className="select-box" style={{ width: "120px" }}>
                    <Filters
                      label="Group By"
                      name="group_by"
                      isLoadingFilters={isLoadingFilters}
                      selectedFilters={selectedFilters.group_by}
                      options={filters.group_by}
                      numberDisplayed={0}
                      includeFilter={false}
                      includeSelectAll={false}
                      enableOnly={false}
                      width="160px"
                    />
                  </div>
                </div>
                <div className="top-filter__left-bottom">
                  <div className="select-box-no-width categories">
                    <p>Referral Category</p>
                    <ToggleableButton
                      isRadio
                      isActive={referralCategory}
                      title="referal-category"
                      onClick={(isActive) =>
                        dispatch(actionCreators.updateReferralToggle(isActive))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="top-filter__right">
                {filtersOptions.map(({ label, name, selectedFilters, options, width }) => (
                  <Filters
                    isLoadingFilters={name === "selected_categories" && isLoadingFilters}
                    key={label}
                    label={label}
                    name={name}
                    selectedFilters={selectedFilters}
                    options={options}
                    numberDisplayed={0}
                    width={width}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <UpdateButtons />
          </div>
        </div>
        <div data-testid="charts-container" className="chart-root">
          {updatesLoading ? (
            <LoadingAnimation text="Updating data..." />
          ) : (
            <>
              <div data-testid="pie-charts-container" className="pie-chart">
                {pieChartsOptions.map(({ data, title }) => {
                  return <PieCharts key={title} data={data} title={title} />;
                })}
              </div>
              <TableWithCustomSearch
                fileName="clients"
                data={data}
                columns={columns}
                colHeaders={colHeaders}
                rawDataLink={showRawDataLink ? rawDataLink : null}
                itemsPerPage={20}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div id="DemographicsModule-root">
      <SectionToggle
        height={690}
        customClass="class-utilization-heatmap withSticky"
        title="Demographics"
        isLoading={isLoading}
        content={content()}
        learnMore={<LearnMore {...learnMoreProps()} />}
      />
    </div>
  );
};
export default DemographicsModule;

const learnMoreProps = () => {
  return {
    content: (
      <>
        <h1>Demographics</h1>
        The Demographics module gives you the ability to easily filter and sort your clients to
        better understand where they come from and how much they spend as well as visit your
        business. There is no calendar control for this module.
        <br />
        <br />
        <b>Client Type:</b>
        <br />
        This filter allows for you to see your demographic breakdown into the following client
        types.
        <br />
        <b>All Clients</b> - Adding all of the three different groupings below.
        <br />
        <b>Members (With Completed Visits)</b> - those members who have had a visit in the last 12
        months
        <br />
        <b>Members (Without Completed Visits)</b> - members who have not had a visit in the last 12
        months
        <br />
        <b>Non-Members</b> - Any client who has had a visit in the last 12 months but doesn’t have
        membership status as of the last iKizmet data update.
        <br />
        <br />
        <i>
          To be counted as a member in this module, the client must have an active agreement based
          on the membership logic used in the dashboard. A member in ClubReady may not be counted in
          iKizmet depending on which membership logic your brand is using.
        </i>
        <br />
        <br />
        <b>Group By: </b> The user can group the table data by the following; Not Grouped
        (default),Referral Source, ZIP Code, Age Grouping, Gender and Birth Month. Only one item in
        the dropdown list can be selected at a time.
        <br />
        <br />
        <b>Referral Categories/Sources: </b> <br />
        When the Referral Categories toggle is turned ON(
        <span style={{ color: "#8fc447", fontWeight: "400" }}>Green</span>
        ), we will display the list of iKizmet categories that each referral source has been
        manually mapped to. Similar to how we display in LASER.
        <br /> With the Referral Categories toggle is turned OFF(
        <span style={{ color: "#fff", fontWeight: "400" }}>White</span>
        ), we will display the referral source (how did you hear about us) in ClubReady. If the
        client is listed as “Unassigned”, that means there is no data in ClubReady for us to show.
        <br />
        <br />
        <b>ZIP Code:</b> Using the ZIP code field in the address section of the ClubReady profile.
        Without data in ClubReady, we will show “Unknown”.
        <br />
        <br />
        <b>Age Grouping:</b> We will use the birthday field to determine the clients age. The age
        groupings are broken up into the following: 1-17, 18-24, 25-34, 35-44, 45-54, 55-64 & 65+.
        If there is not a birthdate in ClubReady, we will list as “Unknown”. <br />
        <br />
        <br />
        <b>Gender:</b> We will show the gender specified in the data in ClubReady. Missing data will
        be shown as “Unknown”.
        <br />
        <br />
        <b>Last Visit:</b> Since we are only seeing data for the last 12 months, you can select the
        month of the client’s last visit.
        <br />
        <br />
        <b>Birth Month:</b> If the data is available, we will show all clients by birth month.
        Missing data will be shown as “Unknown”.
        <br />
        <br />
        <b>Pie Charts:</b> Each of the following metrics will be shown in a visual pie chart,
        Referral Source, ZIP Code, Age Grouping & Gender.
        <br />
        <br />
        <b>Data Table:</b> You can use the search bar to find a specific data.
        <br />
        <br />
        <b>Total Visits:</b> The total number of completed visits from ClubReady.
        <br />
        <br />
        <b>Average Number of Visits:</b> We take the total number of visits and divide by the number
        of months since their first visit.
        <br />
        <br />
        <b>Lifetime Spend:</b> The total amount of revenue the client has generated.
        <br />
        <br />
        <b>Average Spend:</b> We take the total amount of revenue the client has generated and
        divide by the number of months since their profile was created.
        <br />
        <br />
        <i>Note - All data is limited to what is imported into iKizmet. </i>
      </>
    )
  };
};

const errorMessageStyles = {
  textAlign: "center",
  fontSize: "48px",
  wordSpacing: 0,
  lineHeight: "60px"
};

DemographicsModule.propTypes = {
  params: PropTypes.object
};
